/**
 * Delad SEO-byggare för lagerlistningarna (/husbilar-till-salu, /husvagnar-till-salu).
 *
 * Syftet är CTR + rich results: titlar och beskrivningar byggs från verkligt
 * lager (antal, prisspann, märken, nyaste årsmodell) i stället för statisk text,
 * och samma data driver ett komplett schema-graf (BreadcrumbList + CollectionPage
 * + ItemList med riktiga Offer + FAQPage + AutoDealer).
 *
 * Inget här ändrar canonical, robots eller URL-struktur — enbart metadata och
 * strukturerad data på befintliga, redan indexerade sidor.
 */

import { clampDescription } from "@/lib/seo-quality";
import { vehicleSlug } from "./vehicle-slug";

export const SITE = "https://www.akhusbilfritid.se";

export interface ListingLike {
  id: string;

  title: string;
  price?: number | null;
  year?: number | null;
  brand?: string | null;
  model?: string | null;
  mileage?: number | null;
  category?: string | null;
  image?: string;
  images?: string[];
}

export interface ListingStats {
  count: number;
  minPrice: number | null;
  maxPrice: number | null;
  newestYear: number | null;
  brands: string[];
}

const nf = (n: number) => n.toLocaleString("sv-SE");

export function listingStats(items: ListingLike[], category: "husbil" | "husvagn"): ListingStats {
  const scoped = items.filter((i) => (i.category ?? "").toLowerCase().includes(category));
  // Golv på 20 000 kr filtrerar bort tillbehör/felposter så "priser från" blir sant.
  const prices = scoped
    .map((i) => i.price)
    .filter((p): p is number => typeof p === "number" && p >= 20000);

  const years = scoped.map((i) => i.year).filter((y): y is number => typeof y === "number" && y > 1950);
  const brands = Array.from(
    new Set(scoped.map((i) => (i.brand ?? "").trim()).filter(Boolean)),
  ).slice(0, 8);
  return {
    count: scoped.length,
    minPrice: prices.length ? Math.min(...prices) : null,
    maxPrice: prices.length ? Math.max(...prices) : null,
    newestYear: years.length ? Math.max(...years) : null,
    brands,
  };
}

/** Kort, klickvänlig lista: "Kabe, Polar, Adria m.fl." */
function brandPhrase(brands: string[], fallback: string[]): string {
  const list = brands.length >= 2 ? brands : fallback;
  return `${list.slice(0, 3).join(", ")} m.fl.`;
}

export interface ListingHeadInput {
  category: "husbil" | "husvagn";
  items: ListingLike[];
  /** Kanonisk URL för sidan (självrefererande). */
  url: string;
  /** Finsk motsvarighet för hreflang. */
  fiUrl: string;
  faq: { q: string; a: string }[];
  /** Märken som visas på sidan, används som fallback när lagret är tomt. */
  fallbackBrands: string[];
}

export function buildListingHead({ category, items, url, fiUrl, faq, fallbackBrands }: ListingHeadInput) {
  const plural = category === "husbil" ? "husbilar" : "husvagnar";
  const s = listingStats(items, category);
  const scoped = items.filter((i) => (i.category ?? "").toLowerCase().includes(category));

  const countPrefix = s.count > 0 ? `${s.count} ` : "";
  const title = `${countPrefix}${plural} till salu i Kalix | AK Husbil & Fritid`;

  const priceBit = s.minPrice ? ` Priser från ${nf(s.minPrice)} kr.` : "";
  const description = clampDescription(
    `${countPrefix}kontrollerade ${plural} till salu i Kalix — ${brandPhrase(s.brands, fallbackBrands)}` +
      `${priceBit} Fukttestade & gasoltestade. Ring 070-862 97 43.`,
  );



  const keywords = [
    `${plural} till salu`,
    `${plural.slice(0, -1)} till salu`,
    `begagnade ${plural}`,
    `${plural} Kalix`,
    `${plural.slice(0, -1)} Kalix`,
    `${plural} Norrbotten`,
    `${plural} Norrland`,
    `köpa ${plural.slice(0, -1)}`,
    ...(s.brands.length ? s.brands : fallbackBrands),
  ].join(", ");

  const itemList = {
    "@type": "ItemList",
    name: `${plural.charAt(0).toUpperCase()}${plural.slice(1)} i lager hos AK Husbil & Fritid`,
    numberOfItems: s.count,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    itemListElement: scoped.slice(0, 20).map((v, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `${SITE}/fordon/${vehicleSlug(v as never)}`,
      item: {
        "@type": category === "husbil" ? "Motorhome" : "Vehicle",
        name: v.title,
        url: `${SITE}/fordon/${vehicleSlug(v as never)}`,
        image: v.image ?? v.images?.[0],
        brand: v.brand ? { "@type": "Brand", name: v.brand } : undefined,
        vehicleModelDate: v.year ?? undefined,
        mileageFromOdometer: v.mileage
          ? { "@type": "QuantitativeValue", value: v.mileage * 10, unitCode: "KMT" }
          : undefined,
        offers: v.price
          ? {
              "@type": "Offer",
              price: v.price,
              priceCurrency: "SEK",
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/UsedCondition",
              seller: { "@id": `${SITE}/#dealer` },
            }
          : undefined,
      },
    })),
  };

  const graph: Record<string, unknown>[] = [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Fordon till salu", item: `${SITE}/fordon` },
        {
          "@type": "ListItem",
          position: 3,
          name: `${plural.charAt(0).toUpperCase()}${plural.slice(1)} till salu`,
          item: url,
        },
      ],
    },
    {
      "@type": "CollectionPage",
      "@id": `${url}#collection`,
      url,
      name: title,
      description,
      inLanguage: "sv-SE",
      isPartOf: { "@id": `${SITE}/#website` },
      about: [
        { "@type": "Thing", name: `${plural} till salu` },
        { "@type": "Thing", name: `Begagnade ${plural}` },
        { "@type": "Place", name: "Kalix, Norrbotten" },
      ],
      mainEntity: itemList,
      provider: { "@id": `${SITE}/#dealer` },
    },
    {
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "AutoDealer",
      "@id": `${SITE}/#dealer`,
      name: "AK Husbil & Fritid",
      url: `${SITE}/`,
      telephone: "+46708629743",
      priceRange: s.minPrice && s.maxPrice ? `${nf(s.minPrice)}–${nf(s.maxPrice)} kr` : "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Stabsvägen 5",
        postalCode: "952 51",
        addressLocality: "Kalix",
        addressRegion: "Norrbotten",
        addressCountry: "SE",
      },
      areaServed: [
        { "@type": "AdministrativeArea", name: "Norrbotten" },
        { "@type": "Country", name: "Sverige" },
      ],
    },
  ];

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      { property: "og:type", content: "website" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:locale", content: "sv_SE" },
      { property: "og:site_name", content: "AK Husbil & Fritid" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [
      { rel: "canonical", href: url },
      { rel: "alternate", hrefLang: "sv-SE", href: url },
      { rel: "alternate", hrefLang: "fi-FI", href: fiUrl },
      { rel: "alternate", hrefLang: "x-default", href: url },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }),
      },
    ],
  };
}
